import type { BusPoint } from '@/scopes/account/group-responsible/domain/types'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { tokPx } from '@sunwebgroup/ui-components'
import { Banner, Button, RadioButtonField, RadioButtonGroup, Sheet, SwitchField, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'
import { useGroupResponsibleStore } from '../../infrastructure/useGroupResponsibleStore'
import { getBusAssignmentDeadLine, getParticipantsEditingData, getSelectedParticipantsStatus } from '../selectors'

const RadioButtonLabel = styled(Typography)`
  margin-bottom: ${tokPx('primitives_space_4')};
`

const RadioButtonGroupStyled = styled(RadioButtonGroup)`
  & > :not(:last-child) {
    margin-bottom: ${tokPx('primitives_space_8')};
  }
`

const SwitchFieldStyled = styled(SwitchField)`
  margin-bottom: ${tokPx('primitives_space_16')};
`

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokPx('primitives_space_16')};
  margin-top: ${tokPx('primitives_space_16')};
`

const DeadlineBannerStyled = styled(Banner)`
  margin: ${tokPx('primitives_space_16')} 0;
`

const ParticipantStatusBannerStyled = styled(Banner)`
  margin-top: ${tokPx('primitives_space_12')};
`

export function EditSheetPortal() {
  const intl = useIntl()
  const pickupLabelId = React.useId()
  const returnLabelId = React.useId()
  const { selectedCount, totalCount } = useGroupResponsibleStore(getParticipantsEditingData)
  const locale = useConfiguration('locale')
  const { deadLineDate, daysBefore, isOverDeadline } = useGroupResponsibleStore(state => getBusAssignmentDeadLine(state, locale))
  const {
    busPickupPoints,
    busReturnPoints,
    isEditingParticipants,
    toggleIsEditingParticipants,
  } = useGroupResponsibleStore(state => state)
  const [sameAsPickup, setSameAsPickup] = React.useState(true)
  const { hasProcessingParticipants, hasNonBusTransportParticipants } = useGroupResponsibleStore(getSelectedParticipantsStatus)

  const commonSheetStructure = (content: React.ReactNode) => (
    <Sheet.Root open={isEditingParticipants} onOpenChange={open => toggleIsEditingParticipants(open)}>
      <Sheet.Body side="right" hasPadding bottomSideInMobile={false}>
        <Sheet.Header
          closeLabel={intl.formatMessage({ id: 'close' })}
          titleLabel={intl.formatMessage({ id: 'groups_responsible_edit_text' })}
        />
        <Sheet.Content>
          <Typography $variant="display-h-regular">
            {intl.formatMessage(
              {
                id: 'groups_responsible_bus_selected_count',
              },
              {
                selected: selectedCount,
                total: totalCount,
              },
            )}
          </Typography>
          {content}
        </Sheet.Content>
      </Sheet.Body>
    </Sheet.Root>
  )

  const renderBusPoints = (points: BusPoint[], ariaLabelledById: string) => {
    if (points.length === 1) {
      return (
        <Banner
          color="neutral"
          size="M"
          hasIcon
          icon="MapPinOnMap"
          text={intl.formatMessage(
            { id: 'groups_responsible_single_bus_point' },
            { location: points[0].name },
          )}
        />
      )
    }

    return (
      <div role="radiogroup" aria-labelledby={ariaLabelledById}>
        <RadioButtonGroupStyled>
          {points.map(point => (
            <RadioButtonField
              key={point.id}
              label={point.name}
              value={point.id}
              variant="outlined"
            />
          ))}
        </RadioButtonGroupStyled>
      </div>
    )
  }

  let statusBannerProps: { text: string, supportingText?: string } | null = null

  if (hasProcessingParticipants && hasNonBusTransportParticipants) {
    statusBannerProps = {
      text: intl.formatMessage({ id: 'groups_responsible_bus_assignment_mixed_restrictions' }),
      supportingText: intl.formatMessage({ id: 'groups_responsible_bus_assignment_mixed_restrictions_supporting' }),
    }
  }
  else if (hasProcessingParticipants) {
    statusBannerProps = {
      text: intl.formatMessage({ id: 'groups_responsible_bus_assignment_processing' }),
      supportingText: intl.formatMessage({ id: 'groups_responsible_bus_assignment_processing_supporting' }),
    }
  }
  else if (hasNonBusTransportParticipants) {
    statusBannerProps = {
      text: intl.formatMessage({ id: 'groups_responsible_bus_assignment_non_bus_transport' }),
    }
  }

  if (isOverDeadline) {
    return commonSheetStructure(
      <>
        <DeadlineBannerStyled
          color="warning"
          size="M"
          hasIcon
          text={intl.formatMessage({ id: 'groups_responsible_bus_assignment_is_over_deadline_title' })}
          supportingText={intl.formatMessage(
            {
              id: 'groups_responsible_bus_assignment_is_over_deadline_description',
            },
            {
              deadLineDate,
              daysBefore,
            },
          )}
        />
        <Button $fullWidth onClick={() => toggleIsEditingParticipants(false)}>
          {intl.formatMessage({ id: 'close' })}
        </Button>
      </>,
    )
  }

  return commonSheetStructure(
    <>
      <Typography $variant="body-m-regular-snug" as="div">
        {intl.formatMessage(
          {
            id: 'groups_responsible_table_bus_assignment_subtitle',
          },
          {
            deadLineDate,
            daysBefore,
          },
        )}
      </Typography>
      {statusBannerProps && (
        <ParticipantStatusBannerStyled
          color="warning"
          size="M"
          hasIcon
          text={statusBannerProps.text}
          supportingText={statusBannerProps.supportingText}
        />
      )}
      <InputsContainer>
        <div
          role="radiogroup"
          aria-labelledby="groups_responsible_pickup_points"
        >
          <RadioButtonLabel
            as="div"
            $variant="body-m-regular-snug"
            id={pickupLabelId}
          >
            {intl.formatMessage({ id: 'groups_responsible_pickup_point' })}
          </RadioButtonLabel>
          {renderBusPoints(busPickupPoints, pickupLabelId)}
        </div>
        <div
          role="radiogroup"
          aria-labelledby="groups_responsible_return_points"
        >
          <RadioButtonLabel
            as="div"
            $variant="body-m-regular-snug"
            id={returnLabelId}
          >
            {intl.formatMessage({ id: 'groups_responsible_return_point' })}
          </RadioButtonLabel>

          <SwitchFieldStyled
            label={intl.formatMessage({ id: 'groups_responsible_same_as_pickup_point' })}
            variant="outlined"
            checked={sameAsPickup}
            onCheckedChange={setSameAsPickup}
          />
          {!sameAsPickup && renderBusPoints(busReturnPoints, returnLabelId)}
        </div>
        <Button $fullWidth>
          {intl.formatMessage({ id: 'groups_responsible_save' })}
        </Button>
      </InputsContainer>
    </>,
  )
}
